<template>
  <nav class="bnav" role="tablist" aria-label="주요 메뉴">
    <button
      v-for="item in items"
      :key="item.key"
      type="button"
      role="tab"
      class="bnav__item"
      :class="{ 'is-active': item.key === activeTab }"
      :aria-selected="item.key === activeTab"
      :aria-label="ariaLabel(item)"
      @click="go(item)"
    >
      <span class="bnav__icon">
        <el-icon><component :is="item.icon" /></el-icon>
        <NavBadge class="bnav__badge" :count="badge(item)" />
      </span>
      <span class="bnav__label">{{ shortLabel(item) }}</span>
    </button>
  </nav>
</template>

<script>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import NavBadge from './NavBadge.vue'
import { visibleNavItems, navBadgeCount, navAriaLabel, navShortLabel } from './navItems'
import { salesInTabs } from '@/utils/navPrefs'

export default {
  name: 'BottomNav',
  components: { NavBadge },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()

    // surface='tabs' — 결재함은 레일 전용이라 여기 오지 않는다(navItems.js 주석 참고)
    const items = computed(() => visibleNavItems(store.getters, 'tabs', { salesTab: salesInTabs.value }))

    // route.meta.tab 으로 판정한다. 예전 Navigation.vue 는 route.path.startsWith(...)
    // 체인을 썼는데, 경로가 하나 늘 때마다 조건을 손대야 하고 빠뜨리면 조용히 하이라이트가 사라진다.
    // 탭바에 없는 화면(영업 · 바로가기처럼 레일 전용)은 '더보기' 를 켠다 — 폰에서는
    // 더보기를 지나서 들어왔고, 아무 탭도 안 켜져 있으면 지금 어디 있는지 알 수 없다.
    const activeTab = computed(() => {
      const tab = route.meta?.tab || ''
      return items.value.some((i) => i.key === tab) ? tab : 'more'
    })

    function badge(item) {
      return navBadgeCount(item, store.getters)
    }
    function ariaLabel(item) {
      return navAriaLabel(item, badge(item))
    }
    function go(item) {
      if (route.path === item.to) return
      router.push(item.to).catch(() => {})
    }

    return { items, activeTab, badge, ariaLabel, go, shortLabel: navShortLabel }
  }
}
</script>

<style scoped>
.bnav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: var(--z-nav);
  display: flex;
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
  padding-bottom: var(--safe-bottom);
  padding-left: var(--safe-left);
  padding-right: var(--safe-right);
}

.bnav__item {
  flex: 1;
  min-width: 0;
  /* 탭 항목 전체가 탭 대상이다. 아이콘만 누르게 두면 실사용에서 자꾸 빗나간다. */
  min-height: var(--nav-bottom-h);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  border: 0;
  background: none;
  padding: 6px 2px;
  color: var(--color-text-subtle);
  font-family: inherit;
  cursor: pointer;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  transition: color var(--dur-fast) var(--ease);
}

.bnav__item:active {
  background: var(--color-surface-2);
}

.bnav__item.is-active {
  color: var(--color-primary);
}

/* 활성 표시 바 */
.bnav__item.is-active::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 26px;
  height: 3px;
  border-radius: 0 0 var(--r-xs) var(--r-xs);
  background: var(--color-primary);
}

.bnav__icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--ic-xl);
  line-height: 1;
}

.bnav__badge {
  position: absolute;
  top: -6px;
  left: 58%;
}

.bnav__label {
  font-size: var(--fs-2xs);
  font-weight: var(--fw-medium);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* 아주 좁은 기기(320px)에서 라벨이 잘리지 않게 */
@media (max-width: 340px) {
  .bnav__label { font-size: var(--fs-3xs); }
  .bnav__icon { font-size: var(--ic-lg); }
}
</style>
